<template>
  <div class="relative">
    <!-- Tabs -->
    <div class="p-4">
      <div class="bg-slate-100/80 rounded-2xl p-1.5 flex gap-2 shadow-inner">
        <button
          v-for="tab in filterTabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          class="flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold transition-all duration-300 relative"
          :class="[
            activeTab === tab.id
              ? 'bg-white text-primary shadow-lg shadow-primary/20'
              : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
          ]"
        >
          {{ tab.name }}
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="px-4">
      <!-- Error state -->
      <div
        v-if="error && !displayLoading"
        class="flex flex-col items-center justify-center py-16"
      >
        <div
          class="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4"
        >
          <svg
            class="w-10 h-10 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <div class="text-lg font-semibold text-gray-700 mb-2">
          Erreur de chargement
        </div>
        <div class="text-sm text-gray-500 mb-4 text-center max-w-xs">
          {{ error }}
        </div>
        <button
          @click="loadOrders"
          class="px-6 py-2 bg-primary text-white rounded-full font-semibold text-sm hover:bg-primary-dark transition-colors"
        >
          Réessayer
        </button>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="!displayLoading && displayOrders.length === 0"
        class="flex flex-col items-center justify-center py-16"
      >
        <div
          class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4"
        >
          <svg
            class="w-10 h-10 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
        </div>
        <div class="text-lg font-semibold text-gray-700 mb-2">
          Aucune commande
          {{
            activeTab === 'pending'
              ? 'en cours'
              : activeTab === 'in_transit'
                ? 'en livraison'
                : activeTab === 'delivered'
                  ? 'délivrée'
                  : 'annulée'
          }}
        </div>
        <div class="text-sm text-gray-500 text-center max-w-xs mb-4">
          {{
            activeTab === 'pending'
              ? "Vous n'avez aucune commande en cours pour le moment."
              : activeTab === 'in_transit'
                ? "Vous n'avez aucune commande en livraison pour le moment."
                : ''
          }}
        </div>
      </div>

      <!-- Orders list or loading shimmer -->
      <div
        v-else
        class="flex flex-col gap-3"
        :class="{ shimmerContainer: displayLoading }"
      >
        <template v-for="(el, index) in displayOrders" :key="el.id || index">
          <div class="cursor-pointer group" @click="showOrderDetails(el)">
            <div
              class="liquidGlassCard rounded-2xl relative overflow-hidden transition-all duration-300 "
            >
              <!-- Status colored left border -->
              <div
                class="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl"
                :class="getStatusBorderClass(el.status)"
              ></div>

              <!-- Main content -->
              <div class="p-3.5 flex items-center gap-3 pl-5">
                <!-- Product image with shadow -->
                <div class="shimmerTarget relative">
                  <div
                    class="w-20 h-20 rounded-xl overflow-hidden shadow-md border-2 border-white/50 bg-white"
                  >
                    <ImgComponent
                      :src="
                        el.products?.[0]?.principalImageUrl ||
                        '/images/food-default.png'
                      "
                      class="w-full h-full object-cover"
                    />
                  </div>
                  <!-- Status badge on image -->
                  <div
                    class="absolute -bottom-1.5 -right-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold shadow-sm border-2 border-white"
                    :class="getStatusBadgeClass(el.status)"
                  >
                    {{ getStatusShortLabel(el.status) }}
                  </div>
                </div>

                <!-- Content -->
                <div class="flex flex-col flex-1 min-w-0 gap-1.5">
                  <!-- Products names with better typography -->
                  <div class="flex items-start justify-between gap-2">
                    <div
                      class="shimmerTarget title-color font-bold text-base truncate leading-snug flex-1"
                    >
                      {{ getProductsNames(el) }}
                    </div>
                    <!-- Price -->
                    <div class="title-color font-extrabold text-lg">
                      {{ formatPrice(el.totalPrice || el.amount || 0) }}
                    </div>
                  </div>

                  <!-- Meta info - reference on first line -->
                  <div
                    class="flex items-center gap-1.5 text-[11px] text-gray-500"
                  >
                    <svg
                      class="w-3 h-3 opacity-60"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                      />
                    </svg>
                    <span class="font-medium truncate">{{ el.reference }}</span>
                  </div>

                  <!-- Second line: time + items count -->
                  <div class="flex justify-between">
                    <div
                      class="flex items-center gap-3 text-[11px] text-gray-500"
                    >
                      <div class="flex items-center gap-1 shimmerTarget">
                        <svg
                          class="w-3 h-3 opacity-60"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <!-- Countdown timer for active orders, date otherwise -->
                        <template v-if="isActiveOrder(el.status) && getOrderTimerLabel(el)">
                          <span class="font-bold text-orange-500">
                            {{ getOrderTimerLabel(el) }}
                          </span>
                        </template>
                        <template v-else>
                          <span class="flex-shrink">{{ formatDateTimeShort(el.createdAt) }}</span>
                        </template>
                      </div>
                      <div class="flex items-center gap-1 shimmerTarget">
                        <svg
                          class="w-3 h-3 opacity-60"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                          />
                        </svg>
                        <span class="flex-shrink"
                          >{{ el.products?.length || 0 }} article{{
                            (el.products?.length || 0) > 1 ? 's' : ''
                          }}</span
                        >
                      </div>
                    </div>
                    <div class="ml-1 flex items-center gap-2">
                      <button
                        @click.stop="openRiderChat(el)"
                        class="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                      >
                        <svg
                          class="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </button>
                      <svg
                        class="w-5 h-5 text-gray-300 group-hover:text-gray-400 transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <!-- Actions -->
              </div>
            </div>
          </div>
        </template>

        <!-- Shimmer placeholders when loading -->
        <template v-if="displayLoading" v-for="i in 10" :key="`shimmer-${i}`">
          <div class="liquidGlassCard rounded-2xl relative overflow-hidden">
            <div class="p-3.5 flex items-center gap-3 pl-5">
              <div class="w-20 h-20 bg-gray-200 rounded-xl"></div>
              <div class="flex flex-col flex-1 min-w-0 gap-1.5">
                <div class="h-4 bg-gray-200 rounded w-40"></div>
                <div class="h-2 bg-gray-200 rounded w-28"></div>
              </div>
              <div class="w-5 h-5 bg-gray-200 rounded-full"></div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Order Details Dialog -->
    <OrderDetailsDialog ref="detailsDialog" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import { getStore } from '@core/store';
import OrderDetailsDialog from './OrderDetailsDialog.vue';

const detailsDialog = ref(null);

defineEmits(['startNewOrder']);

const activeTab = ref('pending');
const filterTabs = [
  // { name: 'En cours', id: 'pending' },
  { name: 'En livraison', id: 'pending' },
  { name: 'Délivré', id: 'delivered' },
  { name: 'Annulé', id: 'canceled' }
];

const store = getStore('orders');

// Load orders via event
const loadOrders = (status = '') => {
  // Don't pass status to controller - load all and filter locally
  bus.emit(constants.EVENTS.ORDERS.LIST, '');
};

// Get orders from store (with default empty array)
const orders = computed(() => store.state.list?.items || []);

// Use loading and error directly from store.list
const isInitialLoad = ref(true);
const loading = computed(() => store.state.list?.loading || false);
const displayLoading = computed(() => loading.value && isInitialLoad.value);

watch(loading, (newLoading) => {
  if (!newLoading) {
    isInitialLoad.value = false;
  }
});
const error = computed(() => store.state.list?.error || null);

// Filter orders by active tab
const displayOrders = computed(() => {
  if (!orders.value || orders.value.length === 0) return [];

  // Map frontend tab to backend status
  const statusMapping = {
    pending: [
      'created',
      'picked_up',
      'in_transit',
      'pending',
      'confirmed',
      'accepted',
      'preparing',
      'arrived',
      'ready_for_pickup'
    ],
    // in_transit: ['picked_up', 'in_transit'],
    delivered: ['delivered'],
    canceled: ['cancelled', 'canceled']
  };

  const allowedStatuses = statusMapping[activeTab.value] || [];
  return orders.value.filter((order) => allowedStatuses.includes(order.status));
});

const formatPrice = (amount) => {
  if (!amount) return '0 FCFA';
  return (
    new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0
    }).format(amount) + ' FCFA'
  );
};

// Get products names truncated with count
const getProductsNames = (order) => {
  if (!order.products || order.products.length === 0)
    return 'Commande de repas';

  const names = order.products.map((p) => p.title).filter(Boolean);
  if (names.length === 0) return 'Commande de repas';

  if (names.length === 1) return names[0];

  const first = names[0];
  const count = order.products.length;
  return `${first} ${count > 1 ? `+${count - 1} autre${count > 2 ? 's' : ''}` : ''}`;
};

// Format date and time
const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays}j`;

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// Short format for list view
const formatDateTimeShort = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `il y a ${diffMins} min`;
  if (diffHours < 24) return `il y a ${diffHours} h`;
  if (diffDays < 7) return `il y a ${diffDays} jours`;

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short'
  }).format(date);
};

// Get status label in French
const getStatusLabel = (status) => {
  const labels = {
    created: 'En attente',
    pending: 'En attente',
    confirmed: 'Confirmée',
    preparing: 'Préparation',
    arrived: 'Destination atteinte',
    ready_for_pickup: 'Prête',
    picked_up: 'Retirée',
    in_transit: 'En livraison',
    delivered: 'Livrée',
    cancelled: 'Annulée',
    canceled: 'Annulée'
  };
  return labels[status] || status;
};

// Get status badge class
const getStatusClass = (status) => {
  const classes = {
    pending: 'bg-yellow-100 text-yellow-700',
    created: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-blue-100 text-blue-700',
    preparing: 'bg-orange-100 text-orange-700',
    ready_for_pickup: 'bg-purple-100 text-purple-700',
    picked_up: 'bg-indigo-100 text-indigo-700',
    in_transit: 'bg-indigo-100 text-indigo-700',
    arrived: 'bg-indigo-100 text-indigo-700',
    delivered: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
    canceled: 'bg-red-100 text-red-700'
  };
  return classes[status] || 'bg-gray-100 text-gray-700';
};

// Get status dot color class
const getStatusDotClass = (status) => {
  const classes = {
    created: 'bg-yellow-500',
    pending: 'bg-yellow-500',
    confirmed: 'bg-blue-500',
    preparing: 'bg-orange-500',
    ready_for_pickup: 'bg-purple-500',
    picked_up: 'bg-indigo-500',
    in_transit: 'bg-indigo-500',
    arrived: 'bg-indigo-500',
    delivered: 'bg-green-500',
    cancelled: 'bg-red-500',
    canceled: 'bg-red-500'
  };
  return classes[status] || 'bg-gray-400';
};

// Get status border class (left colored border)
const getStatusBorderClass = (status) => {
  const classes = {
    created: 'bg-yellow-400',
    pending: 'bg-yellow-400',
    confirmed: 'bg-blue-400',
    preparing: 'bg-orange-400',
    ready_for_pickup: 'bg-purple-400',
    picked_up: 'bg-indigo-400',
    in_transit: 'bg-indigo-400',
    arrived: 'bg-indigo-400',
    delivered: 'bg-green-400',
    cancelled: 'bg-red-400',
    canceled: 'bg-red-400'
  };
  return classes[status] || 'bg-gray-400';
};

// Get status badge class (on image)
const getStatusBadgeClass = (status) => {
  const classes = {
    created: 'bg-yellow-500 text-white',
    pending: 'bg-yellow-500 text-white',
    confirmed: 'bg-blue-500 text-white',
    preparing: 'bg-orange-500 text-white',
    ready_for_pickup: 'bg-purple-500 text-white',
    picked_up: 'bg-indigo-500 text-white',
    in_transit: 'bg-indigo-500 text-white',
    arrived: 'bg-indigo-500 text-white',
    delivered: 'bg-green-500 text-white',
    cancelled: 'bg-red-500 text-white',
    canceled: 'bg-red-500 text-white'
  };
  return classes[status] || 'bg-gray-500 text-white';
};

// Get short status label (1-2 chars)
const getStatusShortLabel = (status) => {
  const labels = {
    created: '⏳',
    pending: '⏳',
    confirmed: '✓',
    preparing: '👨‍🍳',
    ready_for_pickup: '🔔',
    picked_up: '📦',
    in_transit: '🚀',
    arrived: '🚀',
    delivered: '✓',
    cancelled: '✕'
  };
  return labels[status] || '•';
};

let pollTimer = null;
let tickTimer = null;

// Reactive second tick to force timer re-renders
const timerTick = ref(0);

const ACTIVE_STATUSES = ['created','pending', 'confirmed', 'accepted', 'preparing', 'ready_for_pickup', 'picked_up', 'in_transit', 'arrived'];
const BEFORE_PREPARING = ['created','pending', 'confirmed', 'accepted'];

const isActiveOrder = (status) => ACTIVE_STATUSES.includes(status);

/**
 * Get the preparing timestamp from the order's timeline array.
 */
const getPreparingAt = (order) => {
  const timeline = order.timeline || order.raw?.timeline || [];
  const entry = timeline.find((t) => t.status === 'preparing');
  return entry?.timestamp || null;
};

/**
 * Returns the countdown label for an active order, or null if no timer available.
 * Depends on timerTick to refresh every second.
 */
const getOrderTimerLabel = (order) => {
  void timerTick.value; // reactive dependency — refreshes every second

  if (!isActiveOrder(order.status)) return null;

  const totalMin = order.durationMin || 0;
  if (totalMin === 0) return null;

  // Before preparing: show 'En attente (Xmin)'
  if (BEFORE_PREPARING.includes(order.status)) {
    return `En attente (${totalMin} min)`;
  }

  // After preparing started: countdown from preparing timestamp
  const preparingAt = getPreparingAt(order);
  const startMs = preparingAt ? new Date(preparingAt).getTime() : null;
  if (!startMs) return `En préparation (${totalMin} min)`;

  const targetMs = startMs + totalMin * 60 * 1000;
  const diffMs = targetMs - Date.now();

  if (diffMs <= 0) return 'Bientôt livré';

  const mins = Math.floor(diffMs / 60000);
  const secs = Math.floor((diffMs % 60000) / 1000);
  return `Dans ${mins}min ${secs.toString().padStart(2, '0')}s`;
};

onMounted(() => {
  loadOrders(activeTab.value);

  // Poll orders every 5s
  pollTimer = setInterval(() => {
    loadOrders(activeTab.value);
  }, 5000);

  // Tick every second to refresh countdown labels
  tickTimer = setInterval(() => {
    timerTick.value++;
  }, 1000);
});

onUnmounted(() => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (tickTimer) {
    clearInterval(tickTimer);
    tickTimer = null;
  }
});

function showOrderDetails(order) {
  if (detailsDialog.value) {
    detailsDialog.value.showDetails(order);
  }
}

function openRiderChat(order) {
    if (!order?.rider?.name) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Rider non assigné',
      body: "Aucun rider n'a été assigné à votre commande pour le moment"
    });
    return;
  }
  bus.emit('chat.open_with_rider', order);
}
</script>
